import {FeatureNs} from "@shared/types/feature.namespace";
import {UserNs} from "@shared/types/user.namespace";
import {ScreenNs} from "@shared/types/screen.namespace.ts";

export namespace EstimationNs {

  export enum EstimationMethod {
    HOUR_BASED = 'HB',
    THREE_POINT = 'TP',
    STORY_POINT = 'SP',
    T_SHIRT = "TS"
  }

  export enum MoSCoWPriority {
    MUST_HAVE = 'Must Have',
    SHOULD_HAVE = 'Should Have',
    COULD_HAVE = 'Could Have',
    WONT_HAVE = "Won't Have"
  }

  export enum Priority {
    HIGHEST = 'Highest',
    HIGH = 'High',
    MEDIUM = 'Medium',
    LOW = 'Low',
    LOWEST = 'Lowest'
  }

  export interface FeatureEstimation {
    featureId: string;
    summary: string;
    description: string,
    featureType: FeatureNs.FeatureTypeEnums;
    parentId?: string;
    screens: ScreenNs.Screen[];
    moscow: MoSCoWPriority;
    priority: Priority;
    estimatedHours: number;
    hourlyRate: number;
    isHidden:boolean;
    commentCount?: number;
    isOptional?: boolean;
    // — AI estimation (öneri) —
    aiEstimatedHours?: number;
    aiComplexityScore?: number;   // 1-5 zorluk bandı
    aiConfidence?: number;        // 0-1
    aiReasoning?: Record<string, string>;
    aiSource?: 'AI_SUGGESTED' | 'USER_CONFIRMED';
  }

  // TreeTable için özel node interface'i
  export interface EstimationTreeNode {
    key: string;
    data: FeatureEstimation;
    children?: EstimationTreeNode[];
  }

  export interface EstimationFormData {
    groupName: string;
    type: 'FEATURE' | 'SCREEN';
    method: EstimationMethod;
    level: FeatureNs.FeatureTypeEnums;
    hourlyRate: number;
    currency: string;
  }

  export interface EstimationGroup {
    groupId: number;
    name: string;
    hourlyRate: number;
    currency: string;
    visibleOnPublic: boolean;
    createdBy?: UserNs.AuditUser;
    createDate?: string;
    updatedBy?: UserNs.AuditUser;
    updateDate?: string;
  }

  export interface ProjectEstimation {
    estimationId: number;
    projectId: string;
    type: 'FEATURE' | 'SCREEN';
    method: EstimationMethod;
    level: FeatureNs.FeatureTypeEnums;
    hourlyRate: number;
    currency: string;
    version: number;
    projectName: string;
    company: string;
    client: string;
    productOwner: string;
    categories: string;
    techStacks: string;
    minBudget: number;
    maxBudget: number;
    dateOfRequest: Date;
    deadline: Date;
    groups: EstimationGroup[];
  }

  export interface UpdateEstimationRequest extends EstimationFormData {
    workspaceId: string;
    projectId: string;
  }

  export interface GetEstimationsRequest {
    workspaceId: string;
    projectId: string;
  }

  export interface GetFeatureEstimationsRequest extends GetEstimationsRequest {
    estimationId: number | undefined;
    groupId: number | undefined;
  }

  export interface UpdateFeatureEstimationRequest extends GetEstimationsRequest {
    groupId: number;
    featureId: string;
    moscow?: MoSCoWPriority;
    priority?: Priority;
    estimatedHours?: number;
    hourlyRate?: number;
  }

  export interface ToggleFeatureHiddenRequest extends GetEstimationsRequest {
    groupId: number;
    featureIds: string[];
    hidden: boolean;
  }

  export interface DeleteGroupRequest extends GetEstimationsRequest {
    estimationId: number;
    groupId: number;
  }

  export interface DeleteEstimationRequest extends GetEstimationsRequest {
    estimationId: number;
  }

  export interface GroupSummary {
    groupId: number;
    groupName: string;
    totalEstimatedHours: number;
    totalCost: number;
    visibleOnPublic: boolean;
  }

  export interface GetGroupSummaryRequest extends GetEstimationsRequest {
    estimationId: number;
  }

  export interface UpdateGroupVisibilityRequest extends GetEstimationsRequest {
    estimationId: number;
    groupId: number;
    visibleOnPublic: boolean;
  }

  export interface UpdateGroupRequest extends GetEstimationsRequest {
    estimationId: number;
    groupId: number;
    name: string;
    hourlyRate: number;
    currency: string;
    visibleOnPublic?: boolean;
  }

  export interface UpdateProjectCurrencyRequest extends GetEstimationsRequest {
    estimationId: number;
    currency: string;
  }

  export interface Recipient {
    recipientId: number;
    email: string;
    fullName: string;
  }

  export interface GetRecipientsRequest extends GetEstimationsRequest {
    estimationId: number;
  }

  export interface SendEstimationRequest extends GetEstimationsRequest {
    estimationId: number;
    recipients: Recipient[];
  }

  export interface DeleteRecipientRequest extends GetEstimationsRequest {
    estimationId: number;
    recipientId: number;
  }

  // — AI Estimation —
  export interface AiEstimateRequest extends GetEstimationsRequest {
    estimationId: number;
    groupId: number;
  }

  export interface DiscardAiEstimateRequest extends GetEstimationsRequest {
    estimationId: number;
    groupId: number;
    featureIds: string[];
  }

  export interface AiEstimateResult {
    featureId: string;
    status: 'OK' | 'FAILED';
    error?: string;
    aiEstimatedHours?: number;
    aiComplexityScore?: number;
    aiConfidence?: number;
    aiRangeLow?: number;
    aiRangeHigh?: number;
    factors?: Record<string, unknown>;
    reasoning?: Record<string, string>;
  }

  // — Scope-size (T-shirt) saat config'i —
  export interface EstimationConfig {
    scope?: 'PROJECT' | 'WORKSPACE' | 'DEFAULT';
    hoursXs?: number;
    hoursS?: number;
    hoursM?: number;
    hoursL?: number;
    hoursXl?: number;
    multiplierMin?: number;
    multiplierMax?: number;
  }

  export interface GetEstimationConfigRequest {
    workspaceId: string;
    projectId: string;
    workspaceDefault?: boolean;
  }

  export interface SaveEstimationConfigRequest {
    workspaceId: string;
    projectId: string;
    workspaceDefault?: boolean;
    hoursXs?: number;
    hoursS?: number;
    hoursM?: number;
    hoursL?: number;
    hoursXl?: number;
    multiplierMin?: number;
    multiplierMax?: number;
  }

}
